import { auth } from './auth/state.js';
import { Router } from './router.js';
import { renderLogin } from './screens/login.js';
import { renderSignup } from './screens/signup.js';
import { renderProfile } from './screens/profile.js';
import { renderLeaderboard } from './screens/leaderboard.js';
import { mountNavbar } from './screens/navbar.js';
import { renderLobbyBrowser, unmountLobbyBrowser } from './screens/lobby_browser.js';
import { renderRoom, unmountRoom } from './screens/room.js';

/**
 * App layout:
 *
 *   <body>
 *     <nav id="navbar"/>          ← shown when logged in
 *     <div id="app"/>             ← Three.js canvas host (kept hidden in Phase B)
 *     <div id="hud"/>             ← in-game HUD (kept hidden in Phase B)
 *     <div id="screen"/>          ← overlay container for all screens
 *   </body>
 *
 * Phase B routes:
 *   /          → lobby browser (was: game view; the canvas now mounts inside a room)
 *   /login     → login form
 *   /signup    → signup form
 *   /profile   → user stats
 *   /leaderboard → top 15 + own rank
 *   /room/<id> → inside a Colyseus room (configure factions + map + slots)
 *
 * The Three.js renderer is intentionally NOT booted in Phase B — the in-room
 * map preview will be added in Phase C alongside the actual game start.
 */
async function bootstrap(): Promise<void> {
  const navbarHost = document.getElementById('navbar');
  const screen = document.getElementById('screen');
  const app = document.getElementById('app');
  const hud = document.getElementById('hud');
  if (!navbarHost || !screen || !app || !hud) {
    throw new Error('missing root elements');
  }

  // Phase B: hide the legacy game canvas + HUD by default. They'll come back
  // when we render the in-room map preview / actual game.
  app.style.display = 'none';
  hud.style.display = 'none';

  // Pixel-art cursor click feedback — toggle `body.mouse-down` so CSS can
  // swap the cursor sprite's hotspot offset for a subtle "press" effect.
  document.addEventListener('mousedown', () => document.body.classList.add('mouse-down'));
  document.addEventListener('mouseup', () => document.body.classList.remove('mouse-down'));
  // Drop the class if the mouse leaves the window while still pressed —
  // otherwise the "pressed" cursor sticks indefinitely until the next click.
  window.addEventListener('blur', () => document.body.classList.remove('mouse-down'));

  await auth.refresh();

  const router = new Router(screen);
  mountNavbar(navbarHost, router);

  // Helper: clean any per-screen subscriptions/connections on every navigation.
  const cleanup = (): void => {
    unmountLobbyBrowser();
    unmountRoom();
  };

  router.add({
    path: '/login',
    guestOnly: true,
    render: (c) => {
      cleanup();
      showOverlay(screen);
      renderLogin(c, router);
    },
  });
  router.add({
    path: '/signup',
    guestOnly: true,
    render: (c) => {
      cleanup();
      showOverlay(screen);
      renderSignup(c, router);
    },
  });
  router.add({
    path: '/profile',
    requireAuth: true,
    render: (c) => {
      cleanup();
      showOverlay(screen);
      renderProfile(c);
    },
  });
  router.add({
    path: '/leaderboard',
    requireAuth: true,
    render: async (c) => {
      cleanup();
      showOverlay(screen);
      await renderLeaderboard(c);
    },
  });
  router.add({
    path: '/admin',
    requireAuth: true,
    render: async (c) => {
      cleanup();
      showOverlay(screen);
      const { renderAdmin } = await import('./screens/admin.js');
      await renderAdmin(c);
    },
  });
  router.add({
    path: '/room/:*',
    requireAuth: true,
    render: async (c, pathArg) => {
      cleanup();
      showOverlay(screen);
      await renderRoom(c, router, pathArg);
    },
  });
  router.add({
    path: '/',
    requireAuth: true,
    render: (c) => {
      cleanup();
      showOverlay(screen);
      renderLobbyBrowser(c, router);
    },
  });

  router.start();
}

function showOverlay(screen: HTMLElement): void {
  screen.style.display = '';
}

void bootstrap().catch((err) => {
  console.error(err);
  document.body.innerHTML = `<pre style="color:#f88;padding:24px;">${String(err.stack ?? err)}</pre>`;
});
